import { Moon, Sun, Monitor } from "lucide-react";
import { useTheme } from "next-themes";
import { useEffect, useState, useCallback, memo, useRef } from "react";

// Memoized for performance - prevents re-renders from parent
export const ThemeToggle = memo(function ThemeToggle() {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Avoid hydration mismatch
  useEffect(() => {
    setMounted(true);
    // Enable smooth transitions after initial mount
    requestAnimationFrame(() => {
      document.documentElement.classList.add('theme-transition');
    });
    
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
    };
  }, []);

  // 3-way cycle: light → dark → system → light
  // This allows users to return to automatic system preference detection
  const toggleTheme = useCallback(() => {
    if (theme === "light") {
      setTheme("dark");
    } else if (theme === "dark") {
      setTheme("system");
    } else {
      // system or undefined → light
      setTheme("light");
    }
  }, [theme, setTheme]);

  // Determine which icon to show
  const isSystemMode = mounted ? theme === "system" : false;
  const isDark = mounted 
    ? resolvedTheme === "dark" 
    : typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

  // Get aria label based on current mode
  const getAriaLabel = () => {
    if (isSystemMode) return "Switch to light mode";
    if (isDark) return "Switch to system mode";
    return "Switch to dark mode";
  };

  return (
    <button
      onClick={toggleTheme}
      // Fixed dimensions prevent CLS during hydration
      className="w-10 h-10 min-w-[40px] min-h-[40px] rounded-full bg-muted/80 hover:bg-muted flex items-center justify-center transition-colors duration-200 hover:scale-105 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      aria-label={getAriaLabel()}
      type="button"
    >
      <div className="relative w-5 h-5 flex-shrink-0">
        {/* Sun icon - shown in light mode */}
        <Sun
          className={`absolute inset-0 w-5 h-5 text-primary transition-all duration-300 ${
            !isSystemMode && !isDark ? "opacity-100 rotate-0 scale-100" : "opacity-0 rotate-90 scale-0"
          }`}
          aria-hidden="true"
        />
        {/* Moon icon - shown in dark mode */}
        <Moon
          className={`absolute inset-0 w-5 h-5 text-primary transition-all duration-300 ${
            !isSystemMode && isDark ? "opacity-100 rotate-0 scale-100" : "opacity-0 -rotate-90 scale-0"
          }`}
          aria-hidden="true"
        />
        {/* Monitor icon - shown in system mode */}
        <Monitor
          className={`absolute inset-0 w-5 h-5 text-primary transition-all duration-300 ${
            isSystemMode ? "opacity-100 rotate-0 scale-100" : "opacity-0 rotate-90 scale-0"
          }`}
          aria-hidden="true"
        />
      </div>
    </button>
  );
});
